import { Box, Card, CircularProgress, Grid, Icon, IconButton, Switch, Autocomplete, TextField } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { Form, Formik, useFormikContext } from "formik";
import FormField from "layouts/pages/users/new-user/components/FormField";
import PropTypes from "prop-types";
import { memo, useEffect, useState } from "react";
import { errorAlert, successAlert } from "utils/Functions";
import { get, postNoResponse } from "utils/WebService";
import * as Yup from "yup";

const StatusFichaCreateEdit = ({ id, closeCreateEdit }) => {
    const [isLoadingInfo, setIsLoadingInfo] = useState(false);
    const [gruposList, setGruposList] = useState([]);

    useEffect(() => {
        get("/grupo-usuario/list-all").then((data) => {
            let lista = [];
            if (data && Array.isArray(data.data)) {
                lista = data.data;
            } else if (Array.isArray(data)) {
                lista = data;
            }
            setGruposList(lista);
        });
    }, []);

    const handleSumit = (values, actions) => {
        postNoResponse("/status-ficha/save", values)
            .then(() => {
                successAlert(!id ? "Criado com sucesso!" : "Alterações salvas!", closeCreateEdit);
            }).catch(() => {
                errorAlert(!id ? "Erro ao criar!" : "Erro ao salvar alterações.");
            })
            .finally(() => {
                actions.setSubmitting(false);
            });
    }

    return (
        <Card id="status-ficha-info">
            <MDBox display="flex" alignItems="center" p={3}>
                <IconButton onClick={closeCreateEdit} sx={{ p: 0, paddingRight: 1, paddingTop: 0.75 }}>
                    <MDTypography variant="h5"><Icon>arrow_back</Icon></MDTypography>
                </IconButton>
                <MDTypography variant="h6">{id ? "Editar" : "Adicionar"} Status</MDTypography>
            </MDBox>
            <Formik
                initialValues={{ id: null, ccNome: '', cvAtivo: true, cvIndGlobal: false, cvIndSistema: false, grupos: [] }}
                validationSchema={Yup.object().shape({
                    ccNome: Yup.string().required("Campo Obrigatório.")
                })}
                onSubmit={(values, actions) => {
                    actions.setSubmitting(true);
                    handleSumit(values, actions);
                }}>
                {({ values, errors, touched, isSubmitting, setFieldValue }) => (
                    <Form autoComplete="off">
                        <FetchAndSetData id={id} setIsLoadingInfo={setIsLoadingInfo} closeCreateEdit={closeCreateEdit} />
                        <MDBox px={3}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={4}>
                                    <FormField
                                        loading={isLoadingInfo}
                                        type="text"
                                        label="Nome"
                                        name="ccNome"
                                        value={values.ccNome}
                                        placeholder="Informe o nome..."
                                        error={errors.ccNome?.length > 0 && touched.ccNome}
                                        inputProps={{ sx: { height: '30px' } }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={8}>
                                    <Autocomplete
                                        multiple
                                        options={gruposList}
                                        getOptionLabel={(option) => option.ccNome || ""}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        value={values.grupos}
                                        onChange={(e, value) => setFieldValue("grupos", value)}
                                        disabled={values.cvIndGlobal || values.cvIndSistema || isLoadingInfo}
                                        renderInput={(params) => (
                                            <TextField {...params} variant="standard" label="Equipes Permitidas" placeholder="Selecione as equipes..." />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4} style={{ display: 'flex', alignItems: 'center' }}>
                                    {!isLoadingInfo && (
                                        <MDBox display="flex" alignItems="center" mr={3}>
                                            <Switch size="small" checked={values.cvAtivo || false} onChange={(event) => setFieldValue('cvAtivo', event.target.checked)} />
                                            <MDTypography variant="button" sx={{ fontSize: '11px', marginLeft: 0.5 }}>Ativo</MDTypography>
                                        </MDBox>
                                    )}
                                    {!isLoadingInfo && (
                                        <MDBox display="flex" alignItems="center" mr={3}>
                                            <Switch size="small" checked={values.cvIndGlobal || false} onChange={(event) => {
                                                setFieldValue('cvIndGlobal', event.target.checked);
                                                if (event.target.checked) setFieldValue('grupos', []);
                                            }} />
                                            <MDTypography variant="button" sx={{ fontSize: '11px', marginLeft: 0.5 }}>Global</MDTypography>
                                        </MDBox>
                                    )}
                                    {!isLoadingInfo && (
                                        <MDBox display="flex" alignItems="center">
                                            <Switch size="small" checked={values.cvIndSistema || false} onChange={(event) => {
                                                setFieldValue('cvIndSistema', event.target.checked);
                                                if (event.target.checked) setFieldValue('grupos', []);
                                            }} />
                                            <MDTypography variant="button" sx={{ fontSize: '11px', marginLeft: 0.5 }}>Sistema</MDTypography>
                                        </MDBox>
                                    )}
                                </Grid>
                            </Grid>
                        </MDBox>
                        <Box display="flex" alignItems="center" justifyContent="end" p={2}>
                            <MDButton disabled={isLoadingInfo} size="large" variant="contained" color="info" type="submit" endIcon={isSubmitting ? <CircularProgress color="inherit" size={20} /> : null}>
                                {!isSubmitting && (id ? "Salvar" : "Adicionar")}
                            </MDButton>
                        </Box>
                    </Form>
                )}
            </Formik>
        </Card>
    );
};

const FetchAndSetData = ({ id, setIsLoadingInfo, closeCreateEdit }) => {
    const { setFieldValue } = useFormikContext();
    useEffect(() => {
        if (id) {
            setIsLoadingInfo(true);
            get(`/status-ficha/by-id/${id}`)
                .then(data => {
                    setFieldValue('id', data.id || data.cvId);
                    setFieldValue('ccNome', data.ccNome);
                    setFieldValue('cvAtivo', data.cvAtivo);
                    setFieldValue('cvIndGlobal', data.cvIndGlobal);
                    setFieldValue('cvIndSistema', data.cvIndSistema);
                    setFieldValue('grupos', data.grupos || []);
                    setIsLoadingInfo(false);
                }).catch(() => errorAlert("Não encontrado!", closeCreateEdit));
        }
    }, [id]);
    return null;
};

StatusFichaCreateEdit.propTypes = {
    id: PropTypes.any,
    closeCreateEdit: PropTypes.func.isRequired,
};

export default memo(StatusFichaCreateEdit);
